// Finnish public holidays, flag days (liputuspäivät) and other notable days for the crossword calendar.
// kind: 'holiday' (pyhäpäivä), 'flag' (liputuspäivä), 'other' (notable, no day off or flag)

const easterSunday = (year) => {
  // Anonymous Gregorian algorithm
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
};

const addDays = (date, n) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + n);

// n-th (1-based) given weekday (0 = Sunday) of a month; n = -1 for the last one
const nthWeekday = (year, month, weekday, n) => {
  if (n > 0) {
    const first = new Date(year, month, 1);
    return new Date(year, month, 1 + ((weekday - first.getDay() + 7) % 7) + (n - 1) * 7);
  }
  const last = new Date(year, month + 1, 0);
  return new Date(year, month, last.getDate() - ((last.getDay() - weekday + 7) % 7));
};

// First given weekday on or after a date
const weekdayFrom = (year, month, day, weekday) => {
  const start = new Date(year, month, day);
  return addDays(start, (weekday - start.getDay() + 7) % 7);
};

const key = (date) => `${date.getDate()}-${date.getMonth() + 1}`;

const cache = {};

// Map "d-m" -> [{ name, kind }] for a year
export const getFinnishDays = (year) => {
  if (cache[year]) return cache[year];
  const map = {};
  const add = (date, name, kind) => {
    const k = key(date);
    (map[k] = map[k] || []).push({ name, kind });
  };
  const fixed = (month, day, name, kind) => add(new Date(year, month - 1, day), name, kind);

  const easter = easterSunday(year);

  // Public holidays (also flag days where noted)
  fixed(1, 1, 'Uudenvuodenpäivä', 'holiday');
  fixed(1, 6, 'Loppiainen', 'holiday');
  add(addDays(easter, -2), 'Pitkäperjantai', 'holiday');
  add(easter, 'Pääsiäispäivä', 'holiday');
  add(addDays(easter, 1), '2. pääsiäispäivä', 'holiday');
  fixed(5, 1, 'Vappu', 'holiday');
  add(addDays(easter, 39), 'Helatorstai', 'holiday');
  add(addDays(easter, 49), 'Helluntaipäivä', 'holiday');
  const midsummerEve = weekdayFrom(year, 5, 19, 5);
  add(midsummerEve, 'Juhannusaatto', 'holiday');
  add(addDays(midsummerEve, 1), 'Juhannuspäivä', 'holiday');
  add(weekdayFrom(year, 9, 31, 6), 'Pyhäinpäivä', 'holiday');
  fixed(12, 6, 'Itsenäisyyspäivä', 'holiday');
  fixed(12, 24, 'Jouluaatto', 'holiday');
  fixed(12, 25, 'Joulupäivä', 'holiday');
  fixed(12, 26, 'Tapaninpäivä', 'holiday');

  // Flag days (liputuspäivät)
  fixed(1, 1, 'Uudenvuodenpäivä', 'flag');
  fixed(2, 5, 'Runebergin päivä', 'flag');
  fixed(2, 28, 'Kalevalan päivä', 'flag');
  fixed(3, 19, 'Minna Canthin päivä', 'flag');
  fixed(4, 9, 'Mikael Agricolan päivä', 'flag');
  fixed(4, 27, 'Kansallinen veteraanipäivä', 'flag');
  fixed(5, 1, 'Vappu', 'flag');
  fixed(5, 9, 'Eurooppa-päivä', 'flag');
  add(nthWeekday(year, 4, 0, 2), 'Äitienpäivä', 'flag');
  fixed(6, 4, 'Puolustusvoimain lippujuhlan päivä', 'flag');
  add(addDays(midsummerEve, 1), 'Juhannuspäivä', 'flag');
  add(nthWeekday(year, 7, 6, -1), 'Suomen luonnon päivä', 'flag');
  fixed(10, 10, 'Aleksis Kiven päivä', 'flag');
  fixed(10, 24, 'YK-päivä', 'flag');
  fixed(11, 6, 'Ruotsalaisuuden päivä', 'flag');
  add(nthWeekday(year, 10, 0, 2), 'Isänpäivä', 'flag');
  fixed(12, 6, 'Itsenäisyyspäivä', 'flag');
  fixed(12, 25, 'Joulupäivä', 'flag');

  // Other notable days
  fixed(2, 14, 'Ystävänpäivä', 'other');
  fixed(3, 8, 'Kansainvälinen naistenpäivä', 'other');
  add(addDays(easter, -1), 'Pääsiäislauantai', 'other');
  add(addDays(easter, -7), 'Palmusunnuntai', 'other');
  fixed(4, 30, 'Vappuaatto', 'other');
  fixed(10, 31, 'Halloween', 'other');
  add(nthWeekday(year, 2, 0, -1), 'Kesäaika alkaa', 'other');
  add(nthWeekday(year, 9, 0, -1), 'Talviaika alkaa', 'other');
  fixed(12, 31, 'Uudenvuodenaatto', 'other');

  cache[year] = map;
  return map;
};

// Entries for a date, merged by name so e.g. a public holiday that is also a flag day shows once
export const getDayInfo = (date) => {
  const entries = getFinnishDays(date.getFullYear())[key(date)] || [];
  if (entries.length === 0) return null;
  const byName = {};
  entries.forEach(({ name, kind }) => {
    byName[name] = byName[name] || { name, holiday: false, flag: false };
    if (kind === 'holiday') byName[name].holiday = true;
    if (kind === 'flag') byName[name].flag = true;
  });
  const items = Object.values(byName);
  return {
    items,
    isHoliday: items.some(i => i.holiday),
    isFlag: items.some(i => i.flag),
    // Public holidays win the label, then flag days, then the rest
    label: (items.find(i => i.holiday) || items.find(i => i.flag) || items[0]).name,
    title: items.map(i => `${i.name}${i.holiday ? ' (pyhäpäivä)' : ''}${i.flag ? ' (liputuspäivä)' : ''}`).join('\n'),
  };
};
